import React, { useState, useEffect, useCallback } from 'react';
import axios from 'axios';

const API_BASE = '/speeddate/api';

export default function CouponsList() {
  const [coupons, setCoupons] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showForm, setShowForm] = useState(false);
  const [editing, setEditing] = useState(null);
  const [users, setUsers] = useState([]);
  const [searchTerm, setSearchTerm] = useState('');
  const [searchResults, setSearchResults] = useState([]);
  const [selectedUsers, setSelectedUsers] = useState([]);
  const [formData, setFormData] = useState({
    code: '',
    value: '',
    validFrom: '',
    validTo: '',
    maxUses: '',
    scope: 'all',
    isActive: true
  });

  useEffect(() => {
    fetchCoupons();
    fetchUsers();
  }, []);

  const fetchCoupons = async () => {
    try {
      setLoading(true);
      const res = await axios.get(`${API_BASE}/coupons`);
      setCoupons(res.data);
    } catch (err) {
      console.error('Error fetching coupons', err);
      window.Swal.fire('Errore', 'Impossibile caricare i codici sconto', 'error');
    } finally {
      setLoading(false);
    }
  };

  const fetchUsers = async () => {
    try {
      const res = await axios.get(`${API_BASE}/users`);
      if (Array.isArray(res.data)) setUsers(res.data);
    } catch (err) {
      console.error('Error fetching users', err);
    }
  };

  const handleSearchUser = useCallback(async (term) => {
    setSearchTerm(term);
    if (term.length < 2) {
      setSearchResults([]);
      return;
    }
    try {
      const res = await axios.get(`${API_BASE}/users/search?q=${encodeURIComponent(term)}`);
      setSearchResults(res.data.slice(0, 10));
    } catch (err) {
      console.error('Search error', err);
    }
  }, []);

  const addUser = (user) => {
    if (!selectedUsers.find(u => u.id === user.id)) {
      setSelectedUsers([...selectedUsers, user]);
    }
    setSearchTerm('');
    setSearchResults([]);
  };

  const removeUser = (userId) => {
    setSelectedUsers(selectedUsers.filter(u => u.id !== userId));
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    try {
      const data = {
        ...formData,
        value: parseFloat(formData.value),
        maxUses: formData.maxUses ? parseInt(formData.maxUses) : null,
        userIds: formData.scope === 'specific_users' ? selectedUsers.map(u => u.id) : []
      };

      if (editing) {
        await axios.put(`${API_BASE}/coupons/${editing.id}`, data);
        window.Swal.fire({ title: 'Aggiornato!', icon: 'success', timer: 1500, showConfirmButton: false, toast: true, position: 'top-end' });
      } else {
        await axios.post(`${API_BASE}/coupons`, data);
        window.Swal.fire({ title: 'Creato!', icon: 'success', timer: 1500, showConfirmButton: false, toast: true, position: 'top-end' });
      }
      resetForm();
      fetchCoupons();
    } catch (err) {
      window.Swal.fire('Errore', err.response?.data?.error || 'Errore nel salvataggio', 'error');
    }
  };

  const resetForm = () => {
    setFormData({
      code: '',
      value: '',
      validFrom: '',
      validTo: '',
      maxUses: '',
      scope: 'all',
      isActive: true
    });
    setSelectedUsers([]);
    setEditing(null);
    setShowForm(false);
    setSearchTerm('');
    setSearchResults([]);
  };

  const handleEdit = (coupon) => {
    setEditing(coupon);
    setFormData({
      code: coupon.code,
      value: coupon.value,
      validFrom: coupon.validFrom || '',
      validTo: coupon.validTo || '',
      maxUses: coupon.maxUses || '',
      scope: coupon.scope,
      isActive: coupon.isActive
    });
    // Pre-populate selected users if scope === 'specific_users'
    if (coupon.scope === 'specific_users' && coupon.usages) {
      const usersFromUsages = coupon.usages
        .filter(u => u.User)
        .map(u => u.User);
      setSelectedUsers(usersFromUsages);
    } else {
      setSelectedUsers([]);
    }
    setShowForm(true);
  };

  const handleDelete = async (coupon) => {
    window.Swal.fire({
      title: 'Eliminare questo codice sconto?',
      text: `Codice: ${coupon.code}`,
      icon: 'question',
      showCancelButton: true,
      confirmButtonText: 'Sì, elimina',
      cancelButtonText: 'Annulla',
      confirmButtonColor: '#d33',
      customClass: { popup: 'swal2-glass' }
    }).then(async (result) => {
      if (result.isConfirmed) {
        try {
          await axios.delete(`${API_BASE}/coupons/${coupon.id}`);
          window.Swal.fire({ title: 'Eliminato!', icon: 'success', timer: 1500, showConfirmButton: false, toast: true, position: 'top-end' });
          fetchCoupons();
        } catch (err) {
          window.Swal.fire('Errore', err.response?.data?.error || 'Errore nell\'eliminazione', 'error');
        }
      }
    });
  };

  const isExpired = (coupon) => {
    if (!coupon.validTo) return false;
    const today = new Date().toISOString().slice(0, 10);
    return coupon.validTo < today;
  };

  const isActive = (coupon) => {
    if (!coupon.isActive) return false;
    if (isExpired(coupon)) return false;
    if (coupon.maxUses && coupon.usedCount >= coupon.maxUses) return false;
    return true;
  };

  if (loading) return <div className="text-center py-5"><div className="spinner-border text-primary" /></div>;

  return (
    <div className="row g-4 wow fadeIn">
      <div className="col-12">
        <div className="card border-0 shadow-sm glass">
          <div className="card-header bg-transparent border-0 pt-4 px-4 d-flex justify-content-between align-items-center">
            <h4 className="mb-0 fw-bold"><i className="fas fa-percent text-primary me-2"></i>Codici Sconto</h4>
            <button className="btn btn-primary btn-sm shadow-sm rounded-pill px-3" onClick={() => setShowForm(true)}>
              <i className="fas fa-tag me-2"></i>Emetti Nuovo Codice
            </button>
          </div>
          <div className="card-body px-4">
            {coupons.length === 0 ? (
              <p className="text-muted text-center py-4">
                <i className="fas fa-tag fa-2x mb-2 opacity-50 d-block"></i>
                Nessun codice sconto emesso. Clicca "Emetti Nuovo Codice" per iniziare.
              </p>
            ) : (
              <div className="table-responsive">
                <table className="table table-hover align-middle mb-0 bg-transparent">
                  <thead>
                    <tr>
                      <th className="small text-uppercase fw-bold text-muted border-0 py-3">Codice</th>
                      <th className="small text-uppercase fw-bold text-muted border-0 py-3">Sconto</th>
                      <th className="small text-uppercase fw-bold text-muted border-0 py-3">Validità</th>
                      <th className="small text-uppercase fw-bold text-muted border-0 py-3">Destinato a</th>
                      <th className="small text-uppercase fw-bold text-muted border-0 py-3 text-center">Usi</th>
                      <th className="small text-uppercase fw-bold text-muted border-0 py-3 text-center">Stato</th>
                      <th className="small text-uppercase fw-bold text-muted border-0 py-3 text-end">Azioni</th>
                    </tr>
                  </thead>
                  <tbody>
                    {coupons.map(coupon => (
                      <tr key={coupon.id} className="border-bottom border-light">
                        <td className="py-3">
                          <code className="fw-bold text-primary">{coupon.code}</code>
                        </td>
                        <td className="py-3">
                          <span className="badge bg-success rounded-pill px-3 py-2">
                            {parseFloat(coupon.value).toFixed(0)}%
                          </span>
                        </td>
                        <td className="py-3">
                          <div className="small">
                            {coupon.validFrom && coupon.validTo ? (
                              <>
                                dal {new Date(coupon.validFrom).toLocaleDateString('it-IT')}
                                <br />
                                al {new Date(coupon.validTo).toLocaleDateString('it-IT')}
                              </>
                            ) : coupon.validFrom ? (
                              <>dal {new Date(coupon.validFrom).toLocaleDateString('it-IT')}</>
                            ) : coupon.validTo ? (
                              <>fino al {new Date(coupon.validTo).toLocaleDateString('it-IT')}</>
                            ) : (
                              <span className="text-muted">Senza scadenza</span>
                            )}
                          </div>
                        </td>
                        <td className="py-3">
                          {coupon.scope === 'all' ? (
                            <span className="badge bg-info rounded-pill px-3">Tutti</span>
                          ) : (
                            <span className="badge bg-warning text-dark rounded-pill px-3">
                              Utenti specifici
                              {coupon.usages?.filter(u => u.User).length > 0 && (
                                <span className="ms-1">({coupon.usages.filter(u => u.User).length})</span>
                              )}
                            </span>
                          )}
                        </td>
                        <td className="py-3 text-center">
                          <span className={`badge rounded-pill px-3 ${coupon.maxUses ? 'bg-light text-muted' : 'bg-light text-muted'}`}>
                            {coupon.usedCount || 0}{coupon.maxUses ? ` / ${coupon.maxUses}` : ''}
                          </span>
                        </td>
                        <td className="py-3 text-center">
                          {!coupon.isActive ? (
                            <span className="badge bg-secondary rounded-pill px-3">Disattivato</span>
                          ) : isExpired(coupon) ? (
                            <span className="badge bg-danger rounded-pill px-3">Scaduto</span>
                          ) : coupon.maxUses && coupon.usedCount >= coupon.maxUses ? (
                            <span className="badge bg-secondary rounded-pill px-3">Esaurito</span>
                          ) : (
                            <span className="badge bg-success rounded-pill px-3">Attivo</span>
                          )}
                        </td>
                        <td className="py-3 text-end">
                          <button className="btn btn-link btn-sm text-primary px-2" onClick={() => handleEdit(coupon)} title="Modifica">
                            <i className="fas fa-edit"></i>
                          </button>
                          <button className="btn btn-link btn-sm text-danger px-2" onClick={() => handleDelete(coupon)} title="Elimina">
                            <i className="fas fa-trash-alt"></i>
                          </button>
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            )}

            {/* Usage details per coupon as expandable rows or here */}
            {coupons.filter(c => c.usages?.length > 0).length > 0 && (
              <div className="mt-4">
                <h5 className="fw-bold mb-3"><i className="fas fa-history text-muted me-2"></i>Utilizzi Recenti</h5>
                {coupons.map(coupon => {
                  const usedUsages = (coupon.usages || []).filter(u => u.used);
                  if (usedUsages.length === 0) return null;
                  return (
                    <div key={`usage-${coupon.id}`} className="mb-3 p-3 bg-white bg-opacity-50 rounded-3 border">
                      <strong className="small text-uppercase text-muted">{coupon.code}</strong>
                      <table className="table table-sm mb-0 mt-2">
                        <thead>
                          <tr>
                            <th className="small text-muted border-0">Utente</th>
                            <th className="small text-muted border-0">Email</th>
                            <th className="small text-muted border-0 text-end">Data utilizzo</th>
                          </tr>
                        </thead>
                        <tbody>
                          {usedUsages.map((usage, idx) => (
                            <tr key={idx}>
                              <td className="border-0 small">
                                {usage.User ? `${usage.User.firstName || ''} ${usage.User.lastName || ''}`.trim() || '—' : '—'}
                              </td>
                              <td className="border-0 small">{usage.User?.email || '—'}</td>
                              <td className="border-0 small text-end">
                                {usage.usedAt ? new Date(usage.usedAt).toLocaleString('it-IT') : '—'}
                              </td>
                            </tr>
                          ))}
                        </tbody>
                      </table>
                    </div>
                  );
                })}
              </div>
            )}
          </div>
        </div>
      </div>

      {/* Create/Edit Modal */}
      {showForm && (
        <div className="modal show d-block" style={{ backgroundColor: 'rgba(0,0,0,0.5)', zIndex: 1050 }} onClick={() => resetForm()}>
          <div className="modal-dialog modal-dialog-centered modal-lg" onClick={(e) => e.stopPropagation()}>
            <div className="modal-content border-0 shadow-lg" style={{ borderRadius: '20px' }}>
              <div className="modal-header border-0 pt-4 px-4">
                <h5 className="modal-title fw-bold">
                  <i className="fas fa-tag me-2"></i>
                  {editing ? 'Modifica Codice Sconto' : 'Emetti Nuovo Codice Sconto'}
                </h5>
                <button type="button" className="btn-close" onClick={() => resetForm()}></button>
              </div>
              <form onSubmit={handleSubmit}>
                <div className="modal-body p-4">
                  <div className="row g-3">
                    <div className="col-md-6">
                      <label className="form-label small fw-bold">Codice *</label>
                      <input
                        type="text"
                        className="form-control"
                        placeholder="ES: BENVENUTO10"
                        value={formData.code}
                        onChange={(e) => setFormData({ ...formData, code: e.target.value.toUpperCase() })}
                        required
                      />
                    </div>
                    <div className="col-md-3">
                      <label className="form-label small fw-bold">Sconto % *</label>
                      <input
                        type="number"
                        className="form-control"
                        placeholder="10"
                        min="0"
                        max="100"
                        step="0.5"
                        value={formData.value}
                        onChange={(e) => setFormData({ ...formData, value: e.target.value })}
                        required
                      />
                    </div>
                    <div className="col-md-3">
                      <label className="form-label small fw-bold">Tipo</label>
                      <input
                        type="text"
                        className="form-control"
                        value="Percentuale"
                        disabled
                      />
                    </div>
                    <div className="col-md-6">
                      <label className="form-label small fw-bold">Valido dal</label>
                      <input
                        type="date"
                        className="form-control"
                        value={formData.validFrom}
                        onChange={(e) => setFormData({ ...formData, validFrom: e.target.value })}
                      />
                    </div>
                    <div className="col-md-6">
                      <label className="form-label small fw-bold">Valido al</label>
                      <input
                        type="date"
                        className="form-control"
                        value={formData.validTo}
                        onChange={(e) => setFormData({ ...formData, validTo: e.target.value })}
                      />
                    </div>
                    <div className="col-md-6">
                      <label className="form-label small fw-bold">Utilizzi massimi</label>
                      <input
                        type="number"
                        className="form-control"
                        placeholder="Illimitati"
                        min="1"
                        value={formData.maxUses}
                        onChange={(e) => setFormData({ ...formData, maxUses: e.target.value })}
                      />
                      <div className="form-text">Lascia vuoto per utilizzi illimitati</div>
                    </div>
                    <div className="col-md-6">
                      <label className="form-label small fw-bold">Destinato a</label>
                      <select
                        className="form-select"
                        value={formData.scope}
                        onChange={(e) => setFormData({ ...formData, scope: e.target.value })}
                      >
                        <option value="all">Tutti gli utenti</option>
                        <option value="specific_users">Utenti specifici</option>
                      </select>
                    </div>
                    <div className="col-md-12">
                      <div className="form-check form-switch">
                        <input
                          className="form-check-input"
                          type="checkbox"
                          id="isActive"
                          checked={formData.isActive}
                          onChange={(e) => setFormData({ ...formData, isActive: e.target.checked })}
                        />
                        <label className="form-check-label" htmlFor="isActive">Attivo</label>
                      </div>
                    </div>

                    {formData.scope === 'specific_users' && (
                      <div className="col-12">
                        <div className="border rounded-3 p-3 bg-light">
                          <label className="form-label small fw-bold">Seleziona utenti</label>
                          <div className="position-relative">
                            <input
                              type="text"
                              className="form-control"
                              placeholder="Cerca per nome, cognome o email..."
                              value={searchTerm}
                              onChange={(e) => handleSearchUser(e.target.value)}
                            />
                            {searchResults.length > 0 && (
                              <div className="position-absolute w-100 mt-1 bg-white border rounded-3 shadow-sm" style={{ zIndex: 10, maxHeight: '200px', overflowY: 'auto' }}>
                                {searchResults.map(user => (
                                  <div
                                    key={user.id}
                                    className="px-3 py-2 border-bottom small cursor-pointer hover-bg-light"
                                    style={{ cursor: 'pointer' }}
                                    onClick={() => addUser(user)}
                                  >
                                    <strong>{user.firstName || ''} {user.lastName || ''}</strong>
                                    <span className="text-muted ms-2">({user.email})</span>
                                  </div>
                                ))}
                              </div>
                            )}
                          </div>
                          {selectedUsers.length > 0 && (
                            <div className="mt-2 d-flex flex-wrap gap-2">
                              {selectedUsers.map(user => (
                                <span key={user.id} className="badge bg-primary rounded-pill px-3 py-2 d-flex align-items-center gap-2">
                                  {user.firstName || ''} {user.lastName || ''} - {user.email}
                                  <button
                                    type="button"
                                    className="btn-close btn-close-white"
                                    style={{ fontSize: '10px' }}
                                    onClick={() => removeUser(user.id)}
                                  />
                                </span>
                              ))}
                            </div>
                          )}
                        </div>
                      </div>
                    )}
                  </div>
                </div>
                <div className="modal-footer border-0 px-4 pb-4 pt-0">
                  <button type="button" className="btn btn-light rounded-pill px-4" onClick={() => resetForm()}>Annulla</button>
                  <button type="submit" className="btn btn-primary rounded-pill px-4 shadow-sm">
                    <i className="fas fa-save me-2"></i>{editing ? 'Aggiorna' : 'Crea Codice'}
                  </button>
                </div>
              </form>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}